import { useState, useEffect, useCallback } from "react";
import stockMedicineSearchService from "../api/stockMedicineSearchService";

/**
 * Custom hook for stock medicine search functionality
 * Provides real-time search for individual medicine items in prescription forms
 */
export const useStockMedicineSearch = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  /**
   * Perform search with given parameters
   */
  const search = useCallback(async (searchParams = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const results = await stockMedicineSearchService.searchStockMedicines(
        searchParams
      );
      setSearchResults(results.results || []);
      return results;
    } catch (err) {
      setError(err.message || "Search failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Quick search for autocomplete suggestions
   */
  const quickSearch = useCallback(async (query, limit = 10) => {
    // Trim query to handle whitespace issues
    const trimmedQuery = query ? query.trim() : '';
    
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return [];
    }

    setIsLoading(true);
    try {
      const results = await stockMedicineSearchService.quickSearch(
        trimmedQuery,
        limit
      );
      console.log(
        "🔍 Hook: Received results for query:",
        query,
        "Results:",
        results
      );
      setSuggestions(results);
      return results;
    } catch (err) {
      console.error("Quick search error:", err);
      setSuggestions([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear search results
   */
  const clearResults = useCallback(() => {
    setSearchResults([]);
    setSuggestions([]);
    setError(null);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setSearchResults([]);
    setSuggestions([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    searchResults,
    suggestions,
    isLoading,
    error,
    search,
    quickSearch,
    clearResults,
    reset,
  };
};
